const html_entity_decode = require('./lib/html_entity_decode');
const strip_tags = require('./lib/strip_tags');

/**
 * Sanitizer Class
 *
 * See Source Functions: sanitize_input_helper.php
 */
class Sanitizer {


    /**
     * Removes whitespace from both ends of a string
     * @param value
     * @return {*}
     */
    trim(value) {
        return value.trim();
    }

    /**
     * For any name or title field including name, address, company name, country
     *
     * Sample input: sanitize_text("&#1046;&#32;&#34;&#34; <b>Molly MÁŠA 麗莎 リサ Ли́за 리사 ليزا <span class=\"green\">Smith</span></b> ");
     * Sample output: Ж "" Molly MÁŠA 麗莎 リサ Ли́за 리사 ليزا Smith
     *
     * @private
     * @param string        {string}        The value to sanitize
     * @param validating    {boolean}       Return validation output? Or sanitized string output?
     *
     * @return {string}
     */
    _sanitize_text(string, { validating = false } = {}) {
        let newstring = string;

        // Remove Emojis
        newstring = this.sanitize_emoji(newstring);
        if (validating && newstring !== string) {
            return false;
        }

        // Remove HTML tags
        newstring = strip_tags(newstring);
        if (validating && newstring !== string) {
            return false;
        }

        // Convert HTML special characters to their text equivalents:
        // Ex. &gt;&nbsp;&lt; = < >
        newstring = html_entity_decode(newstring);

        // Remove stray angle brackets and double-quotes
        newstring = newstring.replace(/[\"\<\>]/, '');

        // Replace multiple spaces with single space and trim whitespace.
        // Also note: This will preserve tabs, and newlines... This is only replacing multi-spaces
        newstring = newstring.trim().replace(/  +/g, ' ');

        // Return Sanitization Output
        // if validating, return whether the original string matches exactly what the sanitized value is
        // if not validating, just return the sanitized string value
        if (validating) {
            return true;
        }
        else {
            return newstring;
        }
    }

    /**
     * Return the sanitized string value
     * @public
     * @param string
     * @return {string}
     */
    sanitize_text(string) {
        return this._sanitize_text(string, { validating: false });
    }

    /**
     * Validate whether original string passes sanitization rules.
     * @public
     * @param string
     * @return {boolean}
     */
    sanitize_text_validation(string) {
        return this._sanitize_text(string, { validating: true });
    }

    /**
     * For Emails
     *
     * @private
     * @param string        {string}        The value to sanitize
     * @param validating    {boolean}       Return validation output? Or sanitized string output?
     *
     * @return {string}
     */
    _sanitize_email_content(string, { validating = false } = {}) {
        let newstring = string;

        // Remove Emojis
        newstring = this.sanitize_emoji(newstring);
        if (validating && newstring !== string) {
            return false;
        }

        // Convert HTML special characters to their text equivalents: Ex. &gt;&nbsp;&lt; = < >
        newstring = html_entity_decode(newstring);
        if (validating && newstring !== string) {
            return false;
        }

        // Strip out all HTML tags (except for the array of tags listed below)
        // <b> <blockquote> <br> <em> <i> <img> <li> <ol> <p> <small> <strike> <strong> <sub> <sup> <u> <ul>
        const stripTagsRegex = new RegExp(/<\/*?(?![^>]*?\b(?:b|blockquote|br|em|i|img|li|ol|p|small|strike|strong|sub|sup|u|ul)\b)[^>]*?>/, 'gi');
        newstring = newstring.replace(stripTagsRegex, '');
        if (validating && newstring !== string) {
            return false;
        }

        // Replace double-quotes with single-quotes
        newstring = newstring.replace(/"/gi, `'`);

        // Return Sanitization Output
        // if validating, return whether the original string matches exactly what the sanitized value is
        // if not validating, just return the sanitized string value
        if (validating) {
            return true;
        }
        else {
            return newstring;
        }
    }

    /**
     * Return the sanitized string value
     * @public
     * @param string
     * @return {string}
     */
    sanitize_email_content(string) {
        return this._sanitize_email_content(string, { validating: false });
    }

    /**
     * Validate whether original string passes sanitization rules.
     * @public
     * @param string
     * @return {boolean}
     */
    sanitize_email_content_validation(string) {
        return this._sanitize_email_content(string, { validating: true });
    }

    /**
     * For any field that only needs to remove double-quotes.
     *
     * Sample input: "this\"crazy\"guy"
     * Sample output: thiscrazyguy
     *
     * @public
     * @param string        {string}        The value to sanitize
     *
     * @return {string}
     */
    sanitize_double_quotes(string) {
        let newstring = string;

        // Remove double-quotes.
        newstring = newstring.trim().replace(/\"/, '');

        // Return Sanitization Output
        // if validating, return whether the original string matches exactly what the sanitized value is
        // if not validating, just return the sanitized string value
        return newstring;
    }

    /**
     * For phone and fax numbers
     *
     * Sample input:  sanitize_phone("&#1046;&#32;&#34;&#34; <b>+44 (519) \"435-1202,\" extension 440</b> #$%#$^$%^&$^&");
     * Sample output: +44 (519) 435-1202, x 440
     *
     * @private
     * @param string        {string}        The value to sanitize
     * @param validating    {boolean}       Return validation output? Or sanitized string output?
     *
     * @return {string}
     */
    _sanitize_phone(string, { validating = false } = {}) {
        let newstring = string;

        // Convert HTML special characters to their text equivalents: Ex. &gt;&nbsp;&lt; = < >
        newstring = html_entity_decode(newstring);
        if (validating && newstring !== string) {
            return false;
        }

        // Remove HTML tags
        newstring = strip_tags(newstring);
        if (validating && newstring !== string) {
            return false;
        }

        // Phone extensions: Convert x. (from ext. or ex.) to x
        newstring = newstring.replace(/(x|ext|ex)\.*\s*/, 'x ');

        // Phone numbers: Allow only characters [ 0-9 + - ( ) x / . , ] and spaces (Supports international formats and extension #'s)
        newstring = newstring.replace(/[^0-9+\-\(\)x\/\.,\s]/, '');
        if (validating && newstring !== string) {
            return false;
        }

        // Replace multiple spaces, tabs, and linebreaks with single space and trim whitespace.
        newstring = newstring.trim().replace(/\s+/, ' ');

        // Return Sanitization Output
        // if validating, return whether the original string matches exactly what the sanitized value is
        // if not validating, just return the sanitized string value
        if (validating) {
            return true;
        }
        else {
            return newstring;
        }
    }

    /**
     * Return the sanitized string value
     * @public
     * @param string
     * @return {string}
     */
    sanitize_phone(string) {
        return this._sanitize_phone(string, { validating: false });
    }

    /**
     * Validate whether original string passes sanitization rules.
     * @public
     * @param string
     * @return {boolean}
     */
    sanitize_phone_validation(string) {
        return this._sanitize_phone(string, { validating: true });
    }

    /**
     * For URLs
     *
     * Sample input: sanitize_url("< > { } | \ ^ ~ [ ] ` &#32;&#34;&#34; <b>http://www.voices.com/dogs?a=1&b=2#test%20page</b> ");
     * Sample output: http://www.voices.com/dogs?a=1&b=2#test%20page
     *
     * @private
     * @param string        {string}        The value to sanitize
     * @param validating    {boolean}       Return validation output? Or sanitized string output?
     *
     * @return {string}
     */
    _sanitize_url(string, { validating = false } = {}) {
        let newstring = string;

        // Convert HTML special characters to their text equivalents: Ex. &gt;&nbsp;&lt; = < >
        newstring = html_entity_decode(newstring);
        if (validating && newstring !== string) {
            return false;
        }

        // Remove HTML tags
        newstring = strip_tags(newstring);
        if (validating && newstring !== string) {
            return false;
        }

        // URLS: Remove double-quotes and unsafe characters: " < > { } | \ ^ ~ [ ] `
        newstring = newstring.replace(/[\"\<\>\{\}\|\\\^\~\[\]\`\s]/, '');

        // Trim whitespace
        newstring = newstring.trim();

        // Return Sanitization Output
        // if validating, return whether the original string matches exactly what the sanitized value is
        // if not validating, just return the sanitized string value
        if (validating) {
            return true;
        }
        else {
            return newstring;
        }
    }

    /**
     * Return the sanitized string value
     * @public
     * @param string
     * @return {string}
     */
    sanitize_url(string) {
        return this._sanitize_url(string, { validating: false });
    }

    /**
     * Validate whether original string passes sanitization rules.
     * @public
     * @param string
     * @return {boolean}
     */
    sanitize_url_validation(string) {
        return this._sanitize_url(string, { validating: true });
    }

    /**
     * Sanitize money values
     *
     * @public
     * @param string        {string}        The value to sanitize
     *
     * @return {string}
     */
    sanitize_price(string) {
        let newstring = string;

        // Remove everything except digits 0-9, commas and periods
        newstring = newstring.replace(/[^\d,\.]/, '');

        // If this is a number that uses commas, remove them
        newstring = parseFloat(newstring.replace(',', '')).toString();

        // Strip out all periods except the last
        newstring = newstring.replace(/\.(?=.*\.)/, '');

        // Strip out remaining commas and format as number
        newstring = newstring.replace(',', '');
        newstring = newstring.toFixed(2).toString();

        // Return Sanitization Output
        return newstring;
    }

    /**
     * Sanitize File Names
     *
     * @private
     * @param string        {string}        The value to sanitize
     * @param validating    {boolean}       Return validation output? Or sanitized string output?
     *
     * @return {string}
     */
    _sanitize_file_name(string, { validating = false } = {}) {
        // TODO.
    }

    /**
     * Sanitize input from emoji
     *
     * https://github.com/mathiasbynens/emoji-regex/blob/master/index.js
     *
     * @private
     * @param string        {string}        The value to sanitize
     * @param validating    {boolean}       Return validation output? Or sanitized string output?
     *
     * @return {string}
     */
    _sanitize_emoji(string, { validating = false } = {}) {
        let newstring = string;

        newstring = newstring.replace(/\uD83C\uDFF4\uDB40\uDC67\uDB40\uDC62(?:\uDB40\uDC77\uDB40\uDC6C\uDB40\uDC73|\uDB40\uDC73\uDB40\uDC63\uDB40\uDC74|\uDB40\uDC65\uDB40\uDC6E\uDB40\uDC67)\uDB40\uDC7F|\uD83D\uDC69\u200D\uD83D\uDC69\u200D(?:\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67]))|\uD83D\uDC68(?:\uD83C\uDFFF\u200D(?:\uD83E\uDD1D\u200D\uD83D\uDC68(?:\uD83C[\uDFFB-\uDFFE])|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFE\u200D(?:\uD83E\uDD1D\u200D\uD83D\uDC68(?:\uD83C[\uDFFB-\uDFFD\uDFFF])|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFD\u200D(?:\uD83E\uDD1D\u200D\uD83D\uDC68(?:\uD83C[\uDFFB\uDFFC\uDFFE\uDFFF])|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFC\u200D(?:\uD83E\uDD1D\u200D\uD83D\uDC68(?:\uD83C[\uDFFB\uDFFD-\uDFFF])|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFB\u200D(?:\uD83E\uDD1D\u200D\uD83D\uDC68(?:\uD83C[\uDFFC-\uDFFF])|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\u200D(?:\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D)?\uD83D\uDC68|(?:\uD83D[\uDC68\uDC69])\u200D(?:\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67]))|\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67])|(?:\uD83D[\uDC68\uDC69])\u200D(?:\uD83D[\uDC66\uDC67])|[\u2695\u2696\u2708]\uFE0F|\uD83D[\uDC66\uDC67]|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|(?:\uD83C\uDFFF\u200D[\u2695\u2696\u2708]|\uD83C\uDFFE\u200D[\u2695\u2696\u2708]|\uD83C\uDFFD\u200D[\u2695\u2696\u2708]|\uD83C\uDFFC\u200D[\u2695\u2696\u2708]|\uD83C\uDFFB\u200D[\u2695\u2696\u2708])\uFE0F|\uD83C[\uDFFB-\uDFFF])|\uD83E\uDDD1(?:(?:\uD83C[\uDFFB-\uDFFF])\u200D(?:\uD83E\uDD1D\u200D\uD83E\uDDD1(?:\uD83C[\uDFFB-\uDFFF])|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\u200D(?:\uD83E\uDD1D\u200D\uD83E\uDDD1|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD]))|\uD83D\uDC69(?:\u200D(?:\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D(?:\uD83D[\uDC68\uDC69])|\uD83D[\uDC68\uDC69])|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFF\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFE\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFD\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFC\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFB\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD]))|\uD83D\uDC69\uD83C\uDFFF\u200D\uD83E\uDD1D\u200D(?:\uD83D[\uDC68\uDC69])(?:\uD83C[\uDFFB-\uDFFE])|\uD83D\uDC69\uD83C\uDFFE\u200D\uD83E\uDD1D\u200D(?:\uD83D[\uDC68\uDC69])(?:\uD83C[\uDFFB-\uDFFD\uDFFF])|\uD83D\uDC69\uD83C\uDFFD\u200D\uD83E\uDD1D\u200D(?:\uD83D[\uDC68\uDC69])(?:\uD83C[\uDFFB\uDFFC\uDFFE\uDFFF])|\uD83D\uDC69\uD83C\uDFFC\u200D\uD83E\uDD1D\u200D(?:\uD83D[\uDC68\uDC69])(?:\uD83C[\uDFFB\uDFFD-\uDFFF])|\uD83D\uDC69\uD83C\uDFFB\u200D\uD83E\uDD1D\u200D(?:\uD83D[\uDC68\uDC69])(?:\uD83C[\uDFFC-\uDFFF])|\uD83D\uDC69\u200D\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC69\u200D\uD83D\uDC69\u200D(?:\uD83D[\uDC66\uDC67])|(?:\uD83D\uDC41\uFE0F\u200D\uD83D\uDDE8|\uD83D\uDC69(?:\uD83C\uDFFF\u200D[\u2695\u2696\u2708]|\uD83C\uDFFE\u200D[\u2695\u2696\u2708]|\uD83C\uDFFD\u200D[\u2695\u2696\u2708]|\uD83C\uDFFC\u200D[\u2695\u2696\u2708]|\uD83C\uDFFB\u200D[\u2695\u2696\u2708]|\u200D[\u2695\u2696\u2708])|\uD83C\uDFF3\uFE0F\u200D\u26A7|\uD83E\uDDD1(?:(?:\uD83C[\uDFFB-\uDFFF])\u200D[\u2695\u2696\u2708]|\u200D[\u2695\u2696\u2708])|\uD83D\uDC3B\u200D\u2744|(?:\uD83C[\uDFC3\uDFC4\uDFCA]|\uD83D[\uDC6E\uDC70\uDC71\uDC73\uDC77\uDC81\uDC82\uDC86\uDC87\uDE45-\uDE47\uDE4B\uDE4D\uDE4E\uDEA3\uDEB4-\uDEB6]|\uD83E[\uDD26\uDD35\uDD37-\uDD39\uDD3D\uDD3E\uDDB8\uDDB9\uDDCD-\uDDCF\uDDD6-\uDDDD])(?:\uD83C[\uDFFB-\uDFFF])\u200D[\u2640\u2642]|(?:\u26F9|\uD83C[\uDFCB\uDFCC]|\uD83D\uDD75)(?:\uFE0F\u200D[\u2640\u2642]|(?:\uD83C[\uDFFB-\uDFFF])\u200D[\u2640\u2642])|\uD83C\uDFF4\u200D\u2620|(?:\uD83C[\uDFC3\uDFC4\uDFCA]|\uD83D[\uDC6E-\uDC71\uDC73\uDC77\uDC81\uDC82\uDC86\uDC87\uDE45-\uDE47\uDE4B\uDE4D\uDE4E\uDEA3\uDEB4-\uDEB6]|\uD83E[\uDD26\uDD35\uDD37-\uDD39\uDD3C-\uDD3E\uDDB8\uDDB9\uDDCD-\uDDCF\uDDD6-\uDDDF])\u200D[\u2640\u2642])\uFE0F|\uD83D\uDC69\u200D\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67])|\uD83C\uDFF3\uFE0F\u200D\uD83C\uDF08|\uD83D\uDC69\u200D\uD83D\uDC67|\uD83D\uDC69\u200D\uD83D\uDC66|\uD83D\uDC15\u200D\uD83E\uDDBA|\uD83C\uDDFD\uD83C\uDDF0|\uD83C\uDDF6\uD83C\uDDE6|\uD83C\uDDF4\uD83C\uDDF2|\uD83D\uDC08\u200D\u2B1B|\uD83E\uDDD1(?:\uD83C[\uDFFB-\uDFFF])|\uD83D\uDC69(?:\uD83C[\uDFFB-\uDFFF])|\uD83C\uDDFF(?:\uD83C[\uDDE6\uDDF2\uDDFC])|\uD83C\uDDFE(?:\uD83C[\uDDEA\uDDF9])|\uD83C\uDDFC(?:\uD83C[\uDDEB\uDDF8])|\uD83C\uDDFB(?:\uD83C[\uDDE6\uDDE8\uDDEA\uDDEC\uDDEE\uDDF3\uDDFA])|\uD83C\uDDFA(?:\uD83C[\uDDE6\uDDEC\uDDF2\uDDF3\uDDF8\uDDFE\uDDFF])|\uD83C\uDDF9(?:\uD83C[\uDDE6\uDDE8\uDDE9\uDDEB-\uDDED\uDDEF-\uDDF4\uDDF7\uDDF9\uDDFB\uDDFC\uDDFF])|\uD83C\uDDF8(?:\uD83C[\uDDE6-\uDDEA\uDDEC-\uDDF4\uDDF7-\uDDF9\uDDFB\uDDFD-\uDDFF])|\uD83C\uDDF7(?:\uD83C[\uDDEA\uDDF4\uDDF8\uDDFA\uDDFC])|\uD83C\uDDF5(?:\uD83C[\uDDE6\uDDEA-\uDDED\uDDF0-\uDDF3\uDDF7-\uDDF9\uDDFC\uDDFE])|\uD83C\uDDF3(?:\uD83C[\uDDE6\uDDE8\uDDEA-\uDDEC\uDDEE\uDDF1\uDDF4\uDDF5\uDDF7\uDDFA\uDDFF])|\uD83C\uDDF2(?:\uD83C[\uDDE6\uDDE8-\uDDED\uDDF0-\uDDFF])|\uD83C\uDDF1(?:\uD83C[\uDDE6-\uDDE8\uDDEE\uDDF0\uDDF7-\uDDFB\uDDFE])|\uD83C\uDDF0(?:\uD83C[\uDDEA\uDDEC-\uDDEE\uDDF2\uDDF3\uDDF5\uDDF7\uDDFC\uDDFE\uDDFF])|\uD83C\uDDEF(?:\uD83C[\uDDEA\uDDF2\uDDF4\uDDF5])|\uD83C\uDDEE(?:\uD83C[\uDDE8-\uDDEA\uDDF1-\uDDF4\uDDF6-\uDDF9])|\uD83C\uDDED(?:\uD83C[\uDDF0\uDDF2\uDDF3\uDDF7\uDDF9\uDDFA])|\uD83C\uDDEC(?:\uD83C[\uDDE6\uDDE7\uDDE9-\uDDEE\uDDF1-\uDDF3\uDDF5-\uDDFA\uDDFC\uDDFE])|\uD83C\uDDEB(?:\uD83C[\uDDEE-\uDDF0\uDDF2\uDDF4\uDDF7])|\uD83C\uDDEA(?:\uD83C[\uDDE6\uDDE8\uDDEA\uDDEC\uDDED\uDDF7-\uDDFA])|\uD83C\uDDE9(?:\uD83C[\uDDEA\uDDEC\uDDEF\uDDF0\uDDF2\uDDF4\uDDFF])|\uD83C\uDDE8(?:\uD83C[\uDDE6\uDDE8\uDDE9\uDDEB-\uDDEE\uDDF0-\uDDF5\uDDF7\uDDFA-\uDDFF])|\uD83C\uDDE7(?:\uD83C[\uDDE6\uDDE7\uDDE9-\uDDEF\uDDF1-\uDDF4\uDDF6-\uDDF9\uDDFB\uDDFC\uDDFE\uDDFF])|\uD83C\uDDE6(?:\uD83C[\uDDE8-\uDDEC\uDDEE\uDDF1\uDDF2\uDDF4\uDDF6-\uDDFA\uDDFC\uDDFD\uDDFF])|[#\*0-9]\uFE0F\u20E3|(?:\uD83C[\uDFC3\uDFC4\uDFCA]|\uD83D[\uDC6E\uDC70\uDC71\uDC73\uDC77\uDC81\uDC82\uDC86\uDC87\uDE45-\uDE47\uDE4B\uDE4D\uDE4E\uDEA3\uDEB4-\uDEB6]|\uD83E[\uDD26\uDD35\uDD37-\uDD39\uDD3D\uDD3E\uDDB8\uDDB9\uDDCD-\uDDCF\uDDD6-\uDDDD])(?:\uD83C[\uDFFB-\uDFFF])|(?:\u26F9|\uD83C[\uDFCB\uDFCC]|\uD83D\uDD75)(?:\uD83C[\uDFFB-\uDFFF])|(?:[\u261D\u270A-\u270D]|\uD83C[\uDF85\uDFC2\uDFC7]|\uD83D[\uDC42\uDC43\uDC46-\uDC50\uDC66\uDC67\uDC6B-\uDC6D\uDC72\uDC74-\uDC76\uDC78\uDC7C\uDC83\uDC85\uDCAA\uDD74\uDD7A\uDD90\uDD95\uDD96\uDE4C\uDE4F\uDEC0\uDECC]|\uD83E[\uDD0C\uDD0F\uDD18-\uDD1C\uDD1E\uDD1F\uDD30-\uDD34\uDD36\uDD77\uDDB5\uDDB6\uDDBB\uDDD2-\uDDD5])(?:\uD83C[\uDFFB-\uDFFF])|(?:[\u231A\u231B\u23E9-\u23EC\u23F0\u23F3\u25FD\u25FE\u2614\u2615\u2648-\u2653\u267F\u2693\u26A1\u26AA\u26AB\u26BD\u26BE\u26C4\u26C5\u26CE\u26D4\u26EA\u26F2\u26F3\u26F5\u26FA\u26FD\u2705\u270A\u270B\u2728\u274C\u274E\u2753-\u2755\u2757\u2795-\u2797\u27B0\u27BF\u2B1B\u2B1C\u2B50\u2B55]|\uD83C[\uDC04\uDCCF\uDD8E\uDD91-\uDD9A\uDDE6-\uDDFF\uDE01\uDE1A\uDE2F\uDE32-\uDE36\uDE38-\uDE3A\uDE50\uDE51\uDF00-\uDF20\uDF2D-\uDF35\uDF37-\uDF7C\uDF7E-\uDF93\uDFA0-\uDFCA\uDFCF-\uDFD3\uDFE0-\uDFF0\uDFF4\uDFF8-\uDFFF]|\uD83D[\uDC00-\uDC3E\uDC40\uDC42-\uDCFC\uDCFF-\uDD3D\uDD4B-\uDD4E\uDD50-\uDD67\uDD7A\uDD95\uDD96\uDDA4\uDDFB-\uDE4F\uDE80-\uDEC5\uDECC\uDED0-\uDED2\uDED5-\uDED7\uDEEB\uDEEC\uDEF4-\uDEFC\uDFE0-\uDFEB]|\uD83E[\uDD0C-\uDD3A\uDD3C-\uDD45\uDD47-\uDD78\uDD7A-\uDDCB\uDDCD-\uDDFF\uDE70-\uDE74\uDE78-\uDE7A\uDE80-\uDE86\uDE90-\uDEA8\uDEB0-\uDEB6\uDEC0-\uDEC2\uDED0-\uDED6])|(?:[#\*0-9\xA9\xAE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9\u21AA\u231A\u231B\u2328\u23CF\u23E9-\u23F3\u23F8-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB-\u25FE\u2600-\u2604\u260E\u2611\u2614\u2615\u2618\u261D\u2620\u2622\u2623\u2626\u262A\u262E\u262F\u2638-\u263A\u2640\u2642\u2648-\u2653\u265F\u2660\u2663\u2665\u2666\u2668\u267B\u267E\u267F\u2692-\u2697\u2699\u269B\u269C\u26A0\u26A1\u26A7\u26AA\u26AB\u26B0\u26B1\u26BD\u26BE\u26C4\u26C5\u26C8\u26CE\u26CF\u26D1\u26D3\u26D4\u26E9\u26EA\u26F0-\u26F5\u26F7-\u26FA\u26FD\u2702\u2705\u2708-\u270D\u270F\u2712\u2714\u2716\u271D\u2721\u2728\u2733\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763\u2764\u2795-\u2797\u27A1\u27B0\u27BF\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299]|\uD83C[\uDC04\uDCCF\uDD70\uDD71\uDD7E\uDD7F\uDD8E\uDD91-\uDD9A\uDDE6-\uDDFF\uDE01\uDE02\uDE1A\uDE2F\uDE32-\uDE3A\uDE50\uDE51\uDF00-\uDF21\uDF24-\uDF93\uDF96\uDF97\uDF99-\uDF9B\uDF9E-\uDFF0\uDFF3-\uDFF5\uDFF7-\uDFFF]|\uD83D[\uDC00-\uDCFD\uDCFF-\uDD3D\uDD49-\uDD4E\uDD50-\uDD67\uDD6F\uDD70\uDD73-\uDD7A\uDD87\uDD8A-\uDD8D\uDD90\uDD95\uDD96\uDDA4\uDDA5\uDDA8\uDDB1\uDDB2\uDDBC\uDDC2-\uDDC4\uDDD1-\uDDD3\uDDDC-\uDDDE\uDDE1\uDDE3\uDDE8\uDDEF\uDDF3\uDDFA-\uDE4F\uDE80-\uDEC5\uDECB-\uDED2\uDED5-\uDED7\uDEE0-\uDEE5\uDEE9\uDEEB\uDEEC\uDEF0\uDEF3-\uDEFC\uDFE0-\uDFEB]|\uD83E[\uDD0C-\uDD3A\uDD3C-\uDD45\uDD47-\uDD78\uDD7A-\uDDCB\uDDCD-\uDDFF\uDE70-\uDE74\uDE78-\uDE7A\uDE80-\uDE86\uDE90-\uDEA8\uDEB0-\uDEB6\uDEC0-\uDEC2\uDED0-\uDED6])\uFE0F|(?:[\u261D\u26F9\u270A-\u270D]|\uD83C[\uDF85\uDFC2-\uDFC4\uDFC7\uDFCA-\uDFCC]|\uD83D[\uDC42\uDC43\uDC46-\uDC50\uDC66-\uDC78\uDC7C\uDC81-\uDC83\uDC85-\uDC87\uDC8F\uDC91\uDCAA\uDD74\uDD75\uDD7A\uDD90\uDD95\uDD96\uDE45-\uDE47\uDE4B-\uDE4F\uDEA3\uDEB4-\uDEB6\uDEC0\uDECC]|\uD83E[\uDD0C\uDD0F\uDD18-\uDD1F\uDD26\uDD30-\uDD39\uDD3C-\uDD3E\uDD77\uDDB5\uDDB6\uDDB8\uDDB9\uDDBB\uDDCD-\uDDCF\uDDD1-\uDDDD])/g, '');
        if (validating && newstring !== string) {
            return false;
        }

        // Return Sanitization Output
        // if validating, return whether the original string matches exactly what the sanitized value is
        // if not validating, just return the sanitized string value
        if (validating) {
            return true;
        }
        else {
            return newstring;
        }
    }

    /**
     * Return the sanitized string value
     * @public
     * @param string
     * @return {string}
     */
    sanitize_emoji(string) {
        return this._sanitize_emoji(string, { validating: false });
    }

    /**
     * Validate whether original string passes sanitization rules.
     * @public
     * @param string
     * @return {boolean}
     */
    sanitize_emoji_validation(string) {
        return this._sanitize_emoji(string, { validating: true });
    }

    /**
     * Sanitize alt codes from string
     */
    _sanitize_alt_code(string, { validating = false } = {}) {
        let newstring = string;

        newstring = newstring.replace(/&#(?:\d)+;\s?/g, '');
        if (validating && newstring !== string) {
            return false;
        }

        // Return Sanitization Output
        // if validating, return whether the original string matches exactly what the sanitized value is
        // if not validating, just return the sanitized string value
        if (validating) {
            return true;
        }
        else {
            return newstring;
        }
    }

    /**
     * Return the sanitized string value
     * @public
     * @param string
     * @return {string}
     */
    sanitize_alt_code(string) {
        return this._sanitize_alt_code(string, { validating: false });
    }

    /**
     * Validate whether original string passes sanitization rules.
     * @public
     * @param string
     * @return {boolean}
     */
    sanitize_alt_code_validation(string) {
        return this._sanitize_alt_code(string, { validating: true });
    }
}

module.exports = Sanitizer;
